import { useState, useEffect } from 'react';
import { apiFetch } from '../lib/api.js';

interface OnboardingBannerProps {
  orgId: string;
  onDismiss: () => void;
}

interface OrgData {
  logoUrl?: string | null;
  heroImageUrl?: string | null;
  locations?: Array<{ city?: string | null }>;
  settings?: Record<string, unknown>;
}

interface ProgramsResponse {
  programs: Array<{ id: string }>;
}

export function OnboardingBanner({ orgId, onDismiss }: OnboardingBannerProps) {
  const [hasPrograms, setHasPrograms] = useState(false);
  const [hasAddress, setHasAddress] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const [org, progs] = await Promise.all([
          apiFetch<OrgData>(`/orgs/${orgId}`),
          apiFetch<ProgramsResponse>(`/orgs/${orgId}/programs`),
        ]);

        if (cancelled) return;

        setHasPrograms((progs.programs?.length ?? 0) > 0);
        setHasAddress(
          (org.locations ?? []).some((l) => !!l.city)
        );
        setHasLogo(!!org.logoUrl || !!org.heroImageUrl);
        setLoaded(true);
      } catch {
        setLoaded(true);
      }
    }

    check();

    // Re-check when the tab becomes visible (user returns from Settings)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    // Re-check on focus (covers same-tab navigation back to dashboard)
    const handleFocus = () => check();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [orgId]);

  if (!loaded) return null;

  const completedCount = [hasPrograms, hasAddress, hasLogo].filter(Boolean).length;
  if (completedCount === 3) return null; // All done, hide banner

  const handleDismiss = async () => {
    try {
      await apiFetch(`/orgs/${orgId}/config`, {
        method: 'PUT',
        body: JSON.stringify({ onboarding_banner_dismissed: true }),
      });
    } catch {
      // Non-blocking
    }
    onDismiss();
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">
          Finish setting up your studio
        </h3>
        <button
          onClick={handleDismiss}
          className="text-gray-300 hover:text-gray-500 transition-colors -mt-1"
          title="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Progress dots */}
      <div className="flex items-center gap-4 mb-3 text-xs">
        <span className={`flex items-center gap-1 ${hasPrograms ? 'text-green-600' : 'text-gray-400'}`}>
          {hasPrograms ? (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <span className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 inline-block" />
          )}
          Programs
        </span>
        <span className={`flex items-center gap-1 ${hasAddress ? 'text-green-600' : 'text-gray-400'}`}>
          {hasAddress ? (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <span className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 inline-block" />
          )}
          Address
        </span>
        <span className={`flex items-center gap-1 ${hasLogo ? 'text-green-600' : 'text-gray-400'}`}>
          {hasLogo ? (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <span className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 inline-block" />
          )}
          Branding
        </span>
      </div>

      {/* Action links */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {!hasPrograms && (
          <a href="/app/programs" className="text-primary-600 hover:text-primary-800 font-medium">
            Add programs &rarr;
          </a>
        )}
        {!hasAddress && (
          <a href="/app/settings?tab=location" className="text-primary-600 hover:text-primary-800 font-medium">
            Add your address &rarr;
          </a>
        )}
        {!hasLogo && (
          <a href="/app/settings?tab=organization" className="text-primary-600 hover:text-primary-800 font-medium">
            Set up branding &rarr;
          </a>
        )}
      </div>
    </div>
  );
}
